import React, { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import AgentGraph from "@/components/AgentGraph";
import PostPreview from "@/components/PostPreview";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
    Play, Wand2, Link2, FileText, CheckCircle2, XCircle,
    ChevronRight, Loader2, ChevronDown, ChevronUp, Star
} from "lucide-react";
import { Link } from "react-router-dom";

const EXAMPLES = [
    "Write about learning AI agents",
    "Share a lesson from a failed launch",
    "Explain vector databases to a non-technical founder",
    "Talk about staying focused as a solo founder",
];

const AGENT_KEYS = ["idea", "persona", "strategist", "writing", "review", "brand_voice", "quality"];

const PLATFORMS = [
    { id: "linkedin", label: "LinkedIn", short: "LI" },
    { id: "twitter", label: "X / Twitter", short: "X" },
    { id: "reddit", label: "Reddit", short: "Re" },
    { id: "instagram", label: "Instagram", short: "IG" },
];

const PLATFORM_LABELS = { linkedin: "LinkedIn", twitter: "X / Twitter", reddit: "Reddit", instagram: "Instagram" };

const PLATFORM_HINTS = {
    linkedin: "Full post with hook, body, CTA, hashtags",
    twitter: "Thread or single tweet — punchy, under 280 chars",
    reddit: "Discussion-first, no hashtags, longer form ok",
    instagram: "Caption with line breaks, hashtags, friendly tone",
};

const CHAR_LIMITS = { linkedin: 3000, twitter: 280, reddit: null, instagram: 2200 };

export default function Workspace() {
    const { user } = useAuth();
    const [prompt, setPrompt] = useState("");
    const [running, setRunning] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(-1);
    const [generation, setGeneration] = useState(null);
    const [err, setErr] = useState("");
    const [showGraph, setShowGraph] = useState(false);

    // Sprint 3: Ingestion state
    const [ingestUrl, setIngestUrl] = useState("");
    const [ingesting, setIngesting] = useState(false);
    const [ingestErr, setIngestErr] = useState("");
    const [suggestions, setSuggestions] = useState([]);

    // Active platform tab
    const [activeTab, setActiveTab] = useState("linkedin");

    // Selected platforms for generation (from user prefs, toggleable)
    const [selectedPlatforms, setSelectedPlatforms] = useState(
        () => user?.preferred_platforms || ["linkedin"]
    );

    useEffect(() => {
        const prefs = user?.preferred_platforms;
        if (prefs?.length) setSelectedPlatforms(prefs);
    }, [user]);

    useEffect(() => {
        if (!selectedPlatforms.includes(activeTab)) {
            setActiveTab(selectedPlatforms[0] || "linkedin");
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPlatforms]);

    const togglePlatform = (id) => {
        setSelectedPlatforms(prev =>
            prev.includes(id)
                ? prev.length > 1 ? prev.filter(p => p !== id) : prev
                : [...prev, id]
        );
    };

    const loadSuggestions = async () => {
        try {
            const { data } = await api.get("/suggestions?status=pending");
            setSuggestions(data);
        } catch { /* silent */ }
    };

    useEffect(() => { loadSuggestions(); }, []);

    const simulateProgress = (stop) => {
        let i = 0;
        setCurrentIndex(0);
        const id = setInterval(() => {
            i += 1;
            if (i >= AGENT_KEYS.length || stop.done) { clearInterval(id); return; }
            setCurrentIndex(i);
        }, 5000);
        return id;
    };

    const generate = async () => {
        if (!prompt.trim()) return;
        setErr(""); setGeneration(null); setRunning(true); setShowGraph(true);
        const stop = { done: false };
        const timerId = simulateProgress(stop);
        try {
            const { data } = await api.post("/generate", { prompt: prompt.trim(), platforms: selectedPlatforms });
            setGeneration(data);
            setActiveTab(selectedPlatforms[0] || "linkedin");
            toast.success("Post generated");
        } catch (e) {
            setErr(formatApiError(e));
            toast.error("Generation failed");
        } finally {
            stop.done = true; clearInterval(timerId);
            setRunning(false); setCurrentIndex(-1);
            // Auto-collapse graph after completion
            setTimeout(() => setShowGraph(false), 1500);
        }
    };

    const ingestUrlHandler = async () => {
        if (!ingestUrl.trim()) return;
        setIngestErr(""); setIngesting(true);
        try {
            await api.post("/ingest/url", { url: ingestUrl.trim() });
            setIngestUrl("");
            toast.success("Source ingested — suggestions ready");
            await loadSuggestions();
        } catch (e) {
            setIngestErr(formatApiError(e));
        } finally {
            setIngesting(false);
        }
    };

    const acceptSuggestion = async (s) => {
        await api.put(`/suggestions/${s.id}`, { status: "accepted" });
        setPrompt(s.title + " — " + s.description);
        setSuggestions(prev => prev.filter(x => x.id !== s.id));
        toast.success("Suggestion accepted — edit and generate");
    };

    const dismissSuggestion = async (id) => {
        await api.put(`/suggestions/${id}`, { status: "dismissed" });
        setSuggestions(prev => prev.filter(x => x.id !== id));
    };

    const traces = generation?.prompt_logs || [];
    const drafts = generation?.drafts || {};
    const activeDraft = drafts[activeTab] || generation;
    const finalText = activeDraft
        ? [activeDraft.hook, activeDraft.body, activeDraft.cta, activeDraft.hashtags].filter(Boolean).join("\n\n")
        : "";
    const charLimit = CHAR_LIMITS[activeTab];
    const wordCount = finalText ? finalText.split(/\s+/).filter(Boolean).length : 0;
    const readingTimeSec = Math.ceil(wordCount / 3);

    return (
        <div className="min-h-screen">
            <Navbar />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 sm:mb-8">
                    <div>
                        <div className="lk-label">Your writing room</div>
                        <h1 className="font-heading text-3xl sm:text-5xl mt-2 tracking-tight">What's on your mind?</h1>
                        <p className="text-[var(--lk-muted)] mt-2 text-sm sm:text-base">Paste an article, drop a thought, let Lakshmi do the rest.</p>
                    </div>
                    <Link to="/profile" className="lk-border rounded-full px-4 py-2 text-sm text-[var(--lk-muted)] hover:text-[var(--lk-text)] w-fit">
                        Edit voice profile
                    </Link>
                </div>

                {/* Ingest panel */}
                <div className="lk-border lk-surface rounded-xl p-4 sm:p-5 mb-5">
                    <div className="lk-label mb-1">Read something worth sharing?</div>
                    <p className="text-xs text-[var(--lk-muted)] mb-3">Paste a URL — Lakshmi will extract angles worth posting about.</p>
                    <div className="flex gap-2 sm:gap-3">
                        <div className="flex items-center gap-2 flex-1 lk-border lk-surface-2 rounded-lg px-3 sm:px-4">
                            <Link2 className="w-4 h-4 text-[var(--lk-muted)] shrink-0" />
                            <input
                                value={ingestUrl}
                                onChange={e => setIngestUrl(e.target.value)}
                                onKeyDown={e => e.key === "Enter" && ingestUrlHandler()}
                                placeholder="https://..."
                                className="flex-1 bg-transparent py-3 focus:outline-none text-sm"
                                data-testid="ingest-url-input"
                            />
                        </div>
                        <button
                            onClick={ingestUrlHandler}
                            disabled={ingesting || !ingestUrl.trim()}
                            className="lk-btn-primary rounded-lg px-3 sm:px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50 shrink-0"
                            data-testid="ingest-url-btn"
                        >
                            {ingesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                            <span className="hidden sm:inline">{ingesting ? "Analysing…" : "Analyse"}</span>
                        </button>
                    </div>
                    {ingestErr && <div className="mt-2 text-xs text-red-400">{ingestErr}</div>}

                    {/* Suggestion cards */}
                    {suggestions.length > 0 && (
                        <div className="mt-4">
                            <div className="lk-label mb-3">Lakshmi found these angles</div>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {suggestions.slice(0, 6).map(s => (
                                    <div key={s.id} className="lk-border lk-surface-2 rounded-xl p-4 flex flex-col justify-between gap-3" data-testid={`suggestion-card-${s.id}`}>
                                        <div>
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="font-heading text-base sm:text-lg leading-tight">{s.title}</div>
                                                <span className="shrink-0 text-xs lk-border rounded-full px-2 py-0.5 text-[var(--lk-primary)]">{s.priority_score}/10</span>
                                            </div>
                                            <div className="text-xs text-[var(--lk-muted)] mt-1 line-clamp-2">{s.description}</div>
                                            {s.trend_relevance && (
                                                <div className="mt-2 text-xs text-[var(--lk-secondary)] italic line-clamp-1">{s.trend_relevance}</div>
                                            )}
                                            <div className="flex gap-1 mt-2 flex-wrap">
                                                {(s.platform_fit || []).map(p => (
                                                    <span key={p} className="text-[0.6rem] lk-border rounded px-1.5 py-0.5 text-[var(--lk-muted)]">{PLATFORM_LABELS[p] || p}</span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => acceptSuggestion(s)}
                                                className="flex-1 flex items-center justify-center gap-1 text-xs lk-btn-primary rounded-lg py-1.5"
                                                data-testid={`accept-suggestion-${s.id}`}
                                            >
                                                <CheckCircle2 className="w-3 h-3" /> Use this
                                            </button>
                                            <button
                                                onClick={() => dismissSuggestion(s.id)}
                                                className="flex items-center justify-center text-xs lk-border rounded-lg px-3 py-1.5 text-[var(--lk-muted)] hover:text-red-400"
                                                data-testid={`dismiss-suggestion-${s.id}`}
                                            >
                                                <XCircle className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Platform selector — above textarea */}
                <div className="lk-border lk-surface rounded-xl p-4 sm:p-6 sm:p-8">
                    <div className="mb-4">
                        <label className="lk-label block mb-3">Generating for</label>
                        <div className="flex flex-wrap gap-2">
                            {PLATFORMS.map(p => {
                                const active = selectedPlatforms.includes(p.id);
                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => togglePlatform(p.id)}
                                        title={PLATFORM_HINTS[p.id]}
                                        className={`flex items-center gap-2 rounded-xl px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-all border ${
                                            active
                                                ? "border-[var(--lk-primary)] bg-[var(--lk-primary)]/15 text-[var(--lk-primary)]"
                                                : "border-[var(--lk-border)] bg-[var(--lk-surface-2)] text-[var(--lk-muted)] hover:text-[var(--lk-text)]"
                                        }`}
                                        data-testid={`platform-select-${p.id}`}
                                    >
                                        <span>{p.label}</span>
                                        {active && <CheckCircle2 className="w-3.5 h-3.5" />}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-xs text-[var(--lk-muted)] mt-2">
                            {selectedPlatforms.map(p => PLATFORM_HINTS[p]).join(" · ")}
                        </p>
                    </div>

                    <label className="lk-label block mb-2">Topic or thought</label>
                    <textarea
                        value={prompt}
                        onChange={e => setPrompt(e.target.value)}
                        placeholder="Write about..."
                        rows={3}
                        className="w-full lk-border lk-surface-2 rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--lk-primary)] resize-none text-sm sm:text-base"
                        data-testid="workspace-prompt-input"
                    />
                    <div className="mt-3 flex flex-wrap gap-2">
                        {EXAMPLES.map((ex, i) => (
                            <button key={ex} type="button" onClick={() => setPrompt(ex)}
                                className="text-xs lk-border rounded-full px-3 py-1.5 text-[var(--lk-muted)] hover:text-[var(--lk-text)] hover:border-[var(--lk-primary)]/60"
                                data-testid={`workspace-example-btn-${i}`}>
                                <Wand2 className="inline w-3 h-3 mr-1" />{ex}
                            </button>
                        ))}
                    </div>
                    <div className="mt-5 flex justify-end">
                        <button onClick={generate} disabled={running || !prompt.trim()}
                            className="lk-btn-primary rounded-full px-6 py-3 flex items-center gap-2 disabled:opacity-50 w-full sm:w-auto justify-center"
                            data-testid="workspace-generate-btn">
                            <Play className="w-4 h-4" />
                            {running ? "Agents at work…" : "Generate"}
                        </button>
                    </div>
                </div>

                {err && <div className="mt-6 text-sm text-red-400" data-testid="workspace-error">{err}</div>}

                {/* Agent graph — shown during generation, collapsible */}
                {(running || (generation && showGraph)) && (
                    <div className="mt-6">
                        <button
                            onClick={() => setShowGraph(v => !v)}
                            className="flex items-center gap-2 text-xs text-[var(--lk-muted)] hover:text-[var(--lk-text)] mb-3"
                        >
                            <span className="lk-label">Multi-agent workflow</span>
                            {running
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--lk-primary)]" />
                                : showGraph ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />
                            }
                        </button>
                        {showGraph && (
                            <AgentGraph traces={traces} running={running} currentIndex={currentIndex} />
                        )}
                    </div>
                )}

                {/* Results */}
                {generation && (
                    <div className="mt-8 sm:mt-10">
                        {/* Platform tabs */}
                        {selectedPlatforms.length > 1 && (
                            <div className="flex gap-1 mb-4 lk-border rounded-xl p-1 w-fit overflow-x-auto">
                                {selectedPlatforms.map(p => (
                                    <button key={p} onClick={() => setActiveTab(p)}
                                        className={`px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-lg transition-colors whitespace-nowrap ${activeTab === p ? "bg-[var(--lk-primary)] text-black font-medium" : "text-[var(--lk-muted)] hover:text-[var(--lk-text)]"}`}
                                        data-testid={`platform-tab-${p}`}>
                                        {PLATFORM_LABELS[p] || p}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="grid md:grid-cols-3 gap-5 sm:gap-6">
                            <div className="md:col-span-2">
                                <PostPreview
                                    generation={generation}
                                    platform={activeTab}
                                    draft={activeDraft}
                                />
                            </div>

                            {/* Run summary — user-meaningful only */}
                            <div className="lk-border lk-surface rounded-xl p-5 sm:p-6 flex flex-col gap-4">
                                <div className="lk-label">Post summary</div>

                                {/* Quality score — prominent */}
                                {generation.quality_score != null && (
                                    <div className="lk-border lk-surface-2 rounded-xl p-4 text-center">
                                        <div className="lk-label mb-1">Quality score</div>
                                        <div className="font-heading text-4xl text-[var(--lk-primary)]">
                                            {Math.round(generation.quality_score)}
                                            <span className="text-lg text-[var(--lk-muted)]">/100</span>
                                        </div>
                                        <div className="flex justify-center mt-2 gap-0.5">
                                            {[1,2,3,4,5].map(s => (
                                                <Star key={s} className={`w-3.5 h-3.5 ${s <= Math.round(generation.quality_score / 20) ? "text-[var(--lk-primary)] fill-[var(--lk-primary)]" : "text-[var(--lk-border)]"}`} />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div className="lk-border rounded-lg p-3">
                                        <div className="lk-label">Words</div>
                                        <div className="font-mono mt-1 text-[var(--lk-primary)]">{wordCount}</div>
                                    </div>
                                    <div className="lk-border rounded-lg p-3">
                                        <div className="lk-label">Read time</div>
                                        <div className="font-mono mt-1 text-[var(--lk-primary)]">{readingTimeSec}s</div>
                                    </div>
                                    {charLimit && (
                                        <div className={`lk-border rounded-lg p-3 col-span-2 ${finalText.length > charLimit ? "border-red-400/40" : ""}`}>
                                            <div className="lk-label">Char count</div>
                                            <div className={`font-mono mt-1 text-sm ${finalText.length > charLimit ? "text-red-400" : "text-[var(--lk-primary)]"}`}>
                                                {finalText.length} / {charLimit}
                                                {finalText.length > charLimit && " ⚠ over limit"}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <ScheduleButton generation={generation} platform={activeTab} />

                                <Link to={`/history/${generation.id}`} className="text-xs text-[var(--lk-primary)] hover:underline flex items-center gap-1">
                                    View full trace →
                                </Link>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ScheduleButton({ generation, platform }) {
    const [scheduling, setScheduling] = useState(false);
    const [scheduledAt, setScheduledAt] = useState("");
    const [show, setShow] = useState(false);

    const schedule = async () => {
        if (!scheduledAt) return;
        setScheduling(true);
        try {
            const drafts = generation.drafts || {};
            const draft = drafts[platform] || {};
            const text = [draft.hook, draft.body, draft.cta, draft.hashtags].filter(Boolean).join("\n\n");
            await api.post("/calendar", {
                platform,
                scheduled_at: new Date(scheduledAt).toISOString(),
                draft_text: text,
                title: draft.hook?.slice(0, 80) || generation.prompt?.slice(0, 80),
                generation_id: generation.id,
            });
            toast.success("Scheduled!");
            setShow(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setScheduling(false);
        }
    };

    return (
        <div>
            {!show ? (
                <button onClick={() => setShow(true)}
                    className="w-full lk-border rounded-lg py-2.5 text-sm text-[var(--lk-muted)] hover:text-[var(--lk-text)] flex items-center justify-center gap-2"
                    data-testid="schedule-btn">
                    <ChevronRight className="w-4 h-4" /> Schedule this post
                </button>
            ) : (
                <div className="space-y-2">
                    <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}
                        className="w-full lk-border lk-surface-2 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--lk-primary)]" />
                    <div className="flex gap-2">
                        <button onClick={schedule} disabled={scheduling || !scheduledAt}
                            className="flex-1 lk-btn-primary rounded-lg py-2 text-sm disabled:opacity-50"
                            data-testid="schedule-confirm-btn">
                            {scheduling ? "Scheduling…" : "Confirm"}
                        </button>
                        <button onClick={() => setShow(false)}
                            className="lk-border rounded-lg px-3 py-2 text-sm text-[var(--lk-muted)]">
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
